import {
  authApi,
  type LoginRequest,
  type LoginSession,
  type TeacherSwitchRequest,
} from '@mathflat/domain/Auth'
import { jwtTokenService } from '@mathflat/shared/@modules/auth/jwtTokenService'
import { makeAutoObservable } from 'mobx'
import { AUTH_ROUTES, type AuthRoute, isPublicRoute, RouteName } from 'src/router'

import { academyController } from '@/controller/academy.controller'
import { authController } from '@/controller/auth.controller'
import { CommonError } from '@/dto'
import type FindPassword from '@/page/etc/Login/_dto/FindPassword'
import { envUtils } from '@/utils'
import { getXPlatform } from '@/utils/axios/maxios'
import { buildLoginRedirectSearchParams } from '@/utils/loginRedirect'
import { reporter } from '@/utils/modules/errorReporting/reporter'
import { toastError } from '@/utils/toast'

import { notifyAppAnonymous, notifyAppAuthenticated } from './appAuthBridge.service'
import {
  notifyElectronLoginFromJwtLoginResponse,
  notifyElectronLogout,
} from './electronAuthBridge.service'
import { clearSessionStorageForAcademySwitch, localStorageService } from './storage.service'

class AuthService {
  isKeepSignedInChecked = jwtTokenService.isUsingLocalStorage

  constructor() {
    makeAutoObservable(this)
  }

  async finalizeJwtLogin(
    loginResponse: Pick<LoginSession, 'accessToken' | 'refreshToken' | 'userId'>,
  ) {
    await jwtTokenService.processJwtLoginResponse(loginResponse)
    // 재로그인 전까지만 비노출인 안내를 푼다 — 만료·강제 로그아웃은 authApi.logout() 을 안 거치므로
    // 해제 시점을 로그아웃이 아니라 로그인에 둬야 모든 경로가 덮인다
    localStorageService.removeItem('ESSAY_AI_SCORING_GUIDE_MODAL_CLOSED')
    notifyElectronLoginFromJwtLoginResponse(loginResponse)
  }

  async initializeJwtAuth() {
    // 1. axios 인스턴스 초기화 (env + platform 한 번에)
    const environment = envUtils.isLive
      ? 'live'
      : envUtils.isStaging
        ? 'stg'
        : localStorageService.getItem('DEV_API') ?? 'dev'

    const isNamedEnv = environment === 'live' || environment === 'stg' || environment === 'dev'
    jwtTokenService.init({
      env: isNamedEnv ? environment : 'custom',
      customURL: isNamedEnv ? undefined : environment,
      platform: getXPlatform(),
    })

    // 지금 화면이 어느 학원 것인지 기억해 둔다. 다른 탭에서 학원이 바뀌었는지를 이 값으로 가른다.
    const renderedAcademyId = localStorageService.getItem('ACADEMY_ID')

    // 3. JWT 토큰 서비스 이벤트 핸들러 설정
    const redirectToLoginPath = () => {
      const { pathname, search } = window.location

      // 로그인/SSO 진입 페이지(AUTH 라우트)와 비밀번호 재설정 등 공개 라우트는 비로그인이 정상 상태다.
      // 다른 탭의 TOKEN_NOT_FOUND 응답으로 onTokenNotFound 가 발화해도 /login 으로 튕기지 않게 막는다.
      if (
        AUTH_ROUTES.includes(pathname as AuthRoute) ||
        pathname === RouteName.serverMaintenance ||
        isPublicRoute(pathname)
      ) {
        return
      }

      const loginPath = localStorageService.getItem('LOGIN_PATH') ?? '/login'
      if (pathname === loginPath) return

      // search 만 이어붙이면 원래 pathname 이 유실되므로 (예: /login?redirectUri=...),
      // 현재 위치 전체를 redirectPathAfterLogin 으로 감싸 로그인 후 복귀할 수 있게 한다.
      window.location.replace(
        `${loginPath}?${buildLoginRedirectSearchParams(pathname, search).toString()}`,
      )
    }

    jwtTokenService.setEventHandlers({
      onTokenCleared: () => {
        redirectToLoginPath()
      },
      onTokenNotFound: () => {
        redirectToLoginPath()
      },
      onTokenReceived: () => {
        // 다른 탭이 학원을 옮겼다면 이 탭의 경로에는 이전 학원의 반·학생 id 가 박혀 있다.
        // 제자리 reload 는 그 경로를 새 학원 토큰으로 그대로 다시 요청하므로, 세션 저장소를
        // 비우고 처음부터 들어간다.
        const academyId = localStorageService.getItem('ACADEMY_ID')
        if (renderedAcademyId && academyId && academyId !== renderedAcademyId) {
          clearSessionStorageForAcademySwitch()
          window.location.replace('/')
          return
        }

        // 학원이 그대로면 열어 둔 경로를 지킨다 — 새 탭이 다른 탭에서 토큰을 받아온 경우다.
        window.location.reload()
      },
    })

    // 4. 레거시 토큰 마이그레이션 (jwtTokenService가 알아서 처리)
    await jwtTokenService.initializeAuth()
  }

  // PAY_DENY 고객 로그인시 상담 제안
  async postSubscriptionOffer(...data: Parameters<typeof academyController.postSubscriptionOffer>) {
    return academyController.postSubscriptionOffer(...data)
  }

  async logout() {
    const legacyToken = jwtTokenService.getLegacyToken()
    const refreshToken = jwtTokenService.getRefreshToken()

    const logoutPromises: Promise<unknown>[] = []
    if (legacyToken) {
      logoutPromises.push(
        authApi.logoutLegacy(legacyToken, { mp_event_name: 'T21-01_로그아웃_API' }),
      )
    }
    if (refreshToken) {
      logoutPromises.push(authApi.logout({ refreshToken }))
    }

    // API 요청이 완료될 때까지 대기 (실패해도 계속 진행)
    await Promise.allSettled(logoutPromises)

    jwtTokenService.clearTokens()
    reporter.setUser(null)
    notifyElectronLogout()
    notifyAppAnonymous()
  }

  async loginByJwt(loginRequest: LoginRequest) {
    // 로그인 상태 유지가 체크되어 있으면 localStorage 사용 설정
    if (this.isKeepSignedInChecked) {
      jwtTokenService.useLocalStorage()
    }

    const data = await authApi.login(loginRequest)
    await this.finalizeJwtLogin(data)

    return data
  }

  async loginByNaverWorks(code: string) {
    const data = await authApi.loginWithNaverWorks({
      code,
      userType: 'TEACHER',
      serviceType: 'MATHFLAT',
    })
    await this.finalizeJwtLogin(data)

    return data
  }

  async loginByAiep(code: string) {
    const data = await authApi.loginWithAiep({ code, userType: 'TEACHER' })

    // accessToken 없는 응답이 finalizeJwtLogin → syncLegacyTokenFromJwt 의 빈 Bearer 호출 → 401
    // → clearTokens() 경로로 흘러 정상 세션 토큰까지 삭제되는 것을 막는다.
    if (!data?.accessToken) {
      throw new CommonError('AIEP 로그인 응답이 유효하지 않습니다.', { type: 'client' })
    }

    // SSO 오설정으로 학생 응답이 선생님 진입으로 흘러 TEACHER_ID 에 학생 ID 가 박히는 사고 방지.
    if (data.userType !== 'TEACHER') {
      throw new CommonError('선생님 계정으로 로그인해주세요.', { type: 'client' })
    }

    await this.finalizeJwtLogin(data)

    return data
  }

  /**
   * 같은 사람의 다른 소속 선생님 계정으로 전환한다.
   *
   * 전환은 학원만 바뀌는 게 아니라 userId 자체가 바뀐다(T123 → T456). 그래서 MobX repo 와
   * React Query 캐시가 전부 이전 학원 것으로 남는데, 이를 골라 무효화하는 대신 `/` 로 통째
   * 재진입해 authDaemon 이 처음부터 부트스트랩하게 한다 — 다른 센터 데이터가 한 건도
   * 남지 않아야 한다는 요구(학원 간 격리)에는 전수 무효화보다 재진입이 안전하다.
   * `/` 로 보내는 것은 authDaemon 이 그때만 자기 진입 페이지 계산을 돌리기 때문이다.
   */
  async switchTeacher(teacherId: TeacherSwitchRequest['teacherId']) {
    const refreshToken = jwtTokenService.getRefreshToken()
    // BE 가 이전 리프레시를 무효화하려면 토큰 원문이 필요하다. 없으면 이미 세션이 끊긴 상태라
    // 전환을 시도할 것이 없다 — 예외를 던져 화면을 깨는 대신 알리고 멈춘다.
    if (!refreshToken) {
      toastError('로그인이 만료되었어요. 다시 로그인해주세요.')
      return
    }

    const data = await authApi.switchTeacher({
      refreshToken,
      teacherId,
      // 학원 전환은 restriction 으로 바인그룹(수학 단독 계약)에만 열리므로 MATHFLAT 고정이다.
      serviceType: 'MATHFLAT',
    })

    // 새 토큰을 쓰기 전에 비운다 — 로그인 상태 유지를 끈 세션은 토큰이 세션 저장소에 있어,
    // 순서가 뒤집히면 방금 받은 토큰까지 함께 지워진다.
    clearSessionStorageForAcademySwitch()

    try {
      // 토큰보다 먼저 기록한다. 토큰을 저장하는 순간 다른 탭으로 브로드캐스트가 나가는데,
      // 그 탭은 ACADEMY_ID 로 학원이 바뀐 것을 알아보므로 값이 먼저 바뀌어 있어야 한다.
      // LOGIN_PATH 는 넘기지 않는다 — SSO 진입 경로는 전환과 무관하게 그대로 유지돼야 한다.
      this.saveLoginResponseToStorage(data)
      await this.finalizeJwtLogin(data)
    } finally {
      // 저장이 실패해도 떠난다. 토큰은 메모리부터 새 값으로 바뀌므로 여기서 멈추면 새 학원
      // 토큰으로 이전 학원 화면이 살아 있게 된다. 재진입하면 저장된 토큰이 없어 로그인
      // 화면으로 빠지니, 학원이 섞인 상태로는 남지 않는다.
      window.location.replace('/')
    }
  }

  saveLoginResponseToStorage(
    { userId, academyId }: Pick<LoginSession, 'academyId' | 'userId'>,
    loginPath?: AuthRoute,
    loginId?: string,
  ) {
    // TODO: v1 코드
    localStorageService.setItem('TEACHER_ID')(userId)
    localStorageService.setItem('ACADEMY_ID')(academyId)

    // v2 코드
    localStorageService.setItem('teacher-id')(userId)

    if (loginPath) {
      localStorageService.setItem('LOGIN_PATH')(loginPath)
    }

    if (loginId) {
      localStorageService.setItem('SAVED_LOGIN_ID')(loginId)
    }

    // 앱 알림은 여기서 한다. 토큰 저장은 finalizeJwtLogin 이 먼저 끝내지만 academyId 는 이 함수만
    // 알고 있고, 모든 로그인 경로(일반·게스트·SSO·CRM 쿼리)가 이 함수를 지난다.
    notifyAppAuthenticated({ academyId, teacherId: userId })
  }

  async findPasswordFirstStep(account: FindPassword['account']) {
    await authController.findPasswordFirstStep(account)
  }

  async findPasswordSecondStep(token: FindPassword['resetPassword']['token']) {
    await authController.findPasswordSecondStep({ token })
  }

  async findPasswordThirdStep(passwordField: FindPassword['resetPassword']) {
    await authController.findPasswordThirdStep(passwordField)
  }
}

export const authService = new AuthService()
